"use client"

import { Badge } from "@repo/ui/components/ui/badge"
import { motion, useInView } from "framer-motion"
import { Heart, Leaf, Sparkles, TreePine } from "lucide-react"
import { useRef } from "react"

interface AboutSectionProps {
  content: Record<string, string>
}

export function AboutSection({ content }: AboutSectionProps): JSX.Element {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const title = content["about.title"] || "À propos"
  const intro =
    content["about.intro"] ||
    "Fondée et dirigée par Grégory SIMON, arboriste passionné et créateur de l'application innovante DENDROMAP, RACINES ET CIMES met son expertise au service de la santé et de la sécurité de vos arbres."
  const mission =
    content["about.mission"] ||
    "Notre mission : préserver, entretenir et accompagner la croissance du patrimoine arboré, qu'il s'agisse de jardins privés, de parcs ou d'ensembles publics."
  const approach =
    content["about.approach"] ||
    "Grâce à une approche mêlant savoir-faire traditionnel, techniques d'élagage modernes et outils numériques de pointe, nous proposons des interventions précises, responsables et respectueuses de l'environnement."

  const values = [
    {
      icon: Heart,
      title: content["about.value1.title"] || "Passion",
      description:
        content["about.value1.description"] || "Une passion authentique pour l'arbre et son écosystème",
    },
    {
      icon: Sparkles,
      title: content["about.value2.title"] || "Innovation",
      description:
        content["about.value2.description"] || "Des outils numériques de pointe pour une gestion optimale",
    },
    {
      icon: Leaf,
      title: content["about.value3.title"] || "Engagement",
      description:
        content["about.value3.description"] || "Un engagement durable pour des espaces verts harmonieux",
    },
  ]

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: (delay: number) => ({
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] as const },
    }),
  }

  return (
    <section id="about" className="py-24 bg-forest-tint" ref={ref}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* ─── Section header ────────────────────────────────────────── */}
        <motion.div
          className="flex flex-col items-center gap-4 text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >

          <h2 className="text-4xl font-semibold tracking-tight text-foreground md:text-5xl max-w-2xl">
            Une expertise au service{" "}
            <span className="text-primary">de vos arbres</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start max-w-5xl mx-auto">

          {/* ─── Left: Story text ──────────────────────────────────────── */}
          <div className="flex flex-col gap-8">
            {/* Founder intro */}
            <motion.div
              custom={0.1}
              variants={itemVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="flex flex-col gap-4"
            >
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <TreePine className="size-5 text-primary" aria-hidden />
                </div>
                <p className="text-sm font-semibold text-primary uppercase tracking-widest">
                  Notre histoire
                </p>
              </div>
              <p className="text-lg text-muted-foreground leading-[1.75]">
                Fondée et dirigée par{" "}
                <strong className="text-foreground font-semibold">Grégory SIMON</strong>, arboriste
                passionné et créateur de{" "}
                <strong className="text-foreground font-semibold">DENDROMAP</strong>,{" "}
                <strong className="text-foreground font-semibold">RACINES ET CIMES</strong> met son
                expertise au service de la santé et de la sécurité de vos arbres.
              </p>
            </motion.div>

            {/* Mission card */}
            <motion.div
              custom={0.25}
              variants={itemVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="rounded-2xl border-l-4 border-l-primary bg-card border border-border p-6 shadow-sm"
            >
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground mb-3">
                <Heart className="size-4 text-primary" aria-hidden />
                {content["about.mission.title"] || "Notre mission"}
              </h3>
              <p className="text-muted-foreground leading-[1.7] text-sm">{mission}</p>
            </motion.div>

            {/* Approach */}
            <motion.div
              custom={0.4}
              variants={itemVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <p className="text-muted-foreground leading-[1.75]">{approach}</p>
            </motion.div>
          </div>

          {/* ─── Right: Values ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-5">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                custom={0.3 + index * 0.15}
                variants={itemVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="group flex items-start gap-4 rounded-xl bg-card border border-border p-5 shadow-sm transition-all hover:border-primary/25 hover:shadow-forest"
              >
                <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
                  <value.icon className="size-5 text-primary" aria-hidden />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                    {value.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}

            {/* Conclusion */}
            <motion.p
              custom={0.75}
              variants={itemVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="text-sm text-muted-foreground leading-relaxed italic pt-2 border-t border-border"
            >
              RACINES ET CIMES, c'est l'alliance de la passion de l'arbre, de l'innovation et d'un
              engagement durable pour des espaces verts plus sains et harmonieux.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  )
}
